import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Tabs, Tab, MenuItem, CircularProgress, Alert, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch,
  FormControlLabel, Tooltip, Select, FormControl, InputLabel,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon, Add, Edit, CheckCircle, Cancel, Key as KeyIcon,
  AdminPanelSettings, Lock, LockOpen, Refresh
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI, authAPI, levelsAPI } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

const MODULES = [
  { key:'dashboard',        label:'Dashboard' },
  { key:'enrolments',       label:'Enrolment' },
  { key:'students',         label:'Students' },
  { key:'teachers',         label:'Teachers' },
  { key:'subjects',         label:'Subjects' },
  { key:'assessments',      label:'Assessments' },
  { key:'marks',            label:'Marks Entry' },
  { key:'scoresheet',       label:'Scoresheet' },
  { key:'passing-criteria', label:'Passing Criteria' },
  { key:'reports',          label:'Reports' },
  { key:'settings',         label:'Settings' },
];
const BUILTIN_ROLES = ['admin','teacher','registrar','viewer'];
const ACCESS_OPTS = ['none','view','full'];

export default function Settings() {
  const { enqueueSnackbar } = useSnackbar();
  const { refreshSettings } = useSettings();
  const { user: currentUser } = useAuth();
  const [tab, setTab] = useState(0);

  // School settings
  const [settings, setSettings] = useState({});
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);

  // Academic years
  const [academicYears, setAcademicYears] = useState([]);
  const [yearForm, setYearForm] = useState({ year_label:'', term:'Term 1', start_date:'', end_date:'', is_current:false });
  const [yearDialog, setYearDialog] = useState(false);
  const [editingYear, setEditingYear] = useState(null);

  // Classes
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [classForm, setClassForm] = useState({ name:'', stream:'', level:'', capacity:40, class_teacher_id:'', is_active:true });
  const [classDialog, setClassDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);

  // Users
  const [users, setUsers] = useState([]);
  const [userDialog, setUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ username:'', password:'', full_name:'', role:'viewer', email:'' });
  const [resetDialog, setResetDialog] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [userSaving, setUserSaving] = useState(false);

  // Permissions
  const [permissions, setPermissions] = useState({});
  const [permSaving, setPermSaving] = useState(false);
  const [roles, setRoles] = useState(BUILTIN_ROLES);
  const [addRoleDialog, setAddRoleDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [cloneFrom, setCloneFrom] = useState('viewer');

  const loadAll = () => {
    settingsAPI.get().then(r => { if (r.data.success) setSettings(r.data.data || {}); }).catch(() => {});
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAcademicYears(r.data.data); }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    authAPI.getUsers().then(r => { if (r.data.success) setUsers(r.data.data); }).catch(() => {});
    levelsAPI.getAll().then(r => { if (r.data.success) setLevels(r.data.data); }).catch(() => {});
    authAPI.getRoles().then(r => {
      if (r.data.success) {
        // merge with builtin so admin always first
        const all = ['admin', ...r.data.data.filter(ro => ro !== 'admin')];
        setRoles(all);
      }
    }).catch(() => {});
    authAPI.getPermissions().then(r => {
      if (r.data.success) {
        // Build nested map: { role: { module: { access, own_data_only } } }
        const map = {};
        r.data.data.forEach(p => {
          if (!map[p.role]) map[p.role] = {};
          map[p.role][p.module] = { access: p.access, own_data_only: !!p.own_data_only };
        });
        setPermissions(map);
      }
    }).catch(() => {});
    // Load teachers for class teacher dropdown
    import('../services/api').then(({ teachersAPI }) => {
      teachersAPI.getAll({ limit:500 }).then(r => { if (r.data.success) setTeachers(r.data.data); }).catch(() => {});
    });
  };

  useEffect(() => { loadAll(); }, []);

  // ── School Settings ───────────────────────────────────────────────────────────
  const saveSettings = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      if (logo) {
        const fd = new FormData(); fd.append('logo', logo);
        await settingsAPI.uploadLogo(fd);
      }
      await refreshSettings();
      enqueueSnackbar('Settings saved successfully', { variant:'success' });
    } catch { enqueueSnackbar('Error saving settings', { variant:'error' }); }
    finally { setSaving(false); }
  };

  // ── Academic Years ────────────────────────────────────────────────────────────
  const createYear = async () => {
    try {
      if (editingYear) {
        await settingsAPI.updateAcademicYear(editingYear.id, yearForm);
        enqueueSnackbar('Academic year updated', { variant:'success' });
      } else {
        await settingsAPI.createAcademicYear(yearForm);
        enqueueSnackbar('Academic year created', { variant:'success' });
      }
      setYearDialog(false);
      setEditingYear(null);
      setYearForm({ year_label:'', term:'Term 1', start_date:'', end_date:'', is_current:false });
      settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAcademicYears(r.data.data); });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error saving academic year', { variant:'error' }); }
  };

  const openEditYear = (y) => {
    setEditingYear(y);
    setYearForm({
      year_label: y.year_label || '',
      term: y.term || 'Term 1',
      start_date: y.start_date ? y.start_date.split('T')[0] : '',
      end_date: y.end_date ? y.end_date.split('T')[0] : '',
    });
    setYearDialog(true);
  };

  const deleteYear = async (id) => {
    if (!window.confirm('Delete this academic year/term?')) return;
    try {
      await settingsAPI.deleteAcademicYear(id);
      enqueueSnackbar('Academic year deleted', { variant:'success' });
      settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAcademicYears(r.data.data); });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error deleting academic year', { variant:'error' }); }
  };

  const setCurrentYear = async (id) => {
    try {
      await settingsAPI.setCurrentYear(id);
      enqueueSnackbar('Current year updated', { variant:'success' });
      settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAcademicYears(r.data.data); });
    } catch { enqueueSnackbar('Error updating current year', { variant:'error' }); }
  };

  // ── Classes ───────────────────────────────────────────────────────────────────
  const openCreateClass = () => {
    setEditingClass(null);
    setClassForm({ name:'', stream:'', level:'', level_id:'', capacity:40, class_teacher_id:'', is_active:true });
    setClassDialog(true);
  };
  const openEditClass = (c) => {
    setEditingClass(c);
    setClassForm({ name:c.name||'', stream:c.stream||'', level:c.level||'', level_id:c.level_id||'', capacity:c.capacity||40, class_teacher_id:c.class_teacher_id||'', is_active:!!c.is_active });
    setClassDialog(true);
  };
  const saveClass = async () => {
    try {
      const classPayload = { ...classForm, level_id: classForm.level_id || null };
      if (editingClass) await settingsAPI.updateClass(editingClass.id, classPayload);
      else await settingsAPI.createClass({ ...classForm, level_id: classForm.level_id || null });
      enqueueSnackbar(editingClass?'Class updated':'Class created', { variant:'success' });
      setClassDialog(false);
      settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); });
    } catch { enqueueSnackbar('Error saving class', { variant:'error' }); }
  };
  const deleteClass = async (id) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      await settingsAPI.deleteClass(id);
      enqueueSnackbar('Class deleted', { variant:'success' });
      settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error deleting class', { variant:'error' }); }
  };

  // ── Users ─────────────────────────────────────────────────────────────────────
  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm({ username:'', password:'', full_name:'', role:'viewer', email:'' });
    setUserDialog(true);
  };
  const openEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ username:u.username||'', password:'', full_name:u.full_name||'', role:u.role||'viewer', email:u.email||'' });
    setUserDialog(true);
  };
  const saveUser = async () => {
    setUserSaving(true);
    try {
      if (editingUser) {
        await authAPI.updateUser(editingUser.id, { role:userForm.role, email:userForm.email, is_active:1 });
        enqueueSnackbar('User updated', { variant:'success' });
      } else {
        await authAPI.createUser(userForm);
        enqueueSnackbar('User created — they must change password on first login', { variant:'success' });
      }
      setUserDialog(false);
      authAPI.getUsers().then(r => { if (r.data.success) setUsers(r.data.data); });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Error saving user', { variant:'error' });
    } finally { setUserSaving(false); }
  };
  const toggleUser = async (id) => {
    try {
      await authAPI.toggleUser(id);
      authAPI.getUsers().then(r => { if (r.data.success) setUsers(r.data.data); });
    } catch { enqueueSnackbar('Error toggling user', { variant:'error' }); }
  };
  const doResetPassword = async () => {
    try {
      await authAPI.resetPassword(resetUserId, { new_password: resetPwd });
      enqueueSnackbar('Password reset — user must change on next login', { variant:'success' });
      setResetDialog(false);
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant:'error' }); }
  };

  // ── Permissions ───────────────────────────────────────────────────────────────
  const updatePerm = (role, module, field, value) => {
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], [module]: { ...(prev[role]?.[module] || {}), [field]: value } }
    }));
  };
  const savePermissions = async () => {
    setPermSaving(true);
    try {
      const promises = [];
      for (const role of roles) {
        for (const mod of MODULES) {
          const p = permissions[role]?.[mod.key] || { access:'none', own_data_only:false };
          promises.push(authAPI.updatePermission({ role, module:mod.key, access:p.access, own_data_only:p.own_data_only?1:0 }));
        }
      }
      await Promise.all(promises);
      enqueueSnackbar('Permissions saved successfully', { variant:'success' });
    } catch { enqueueSnackbar('Error saving permissions', { variant:'error' }); }
    finally { setPermSaving(false); }
  };

  const handleAddRole = async () => {
    const name = newRoleName.trim().toLowerCase().replace(/\s+/g, '_');
    if (!name) return enqueueSnackbar('Enter a role name', { variant:'warning' });
    try {
      await authAPI.addRole({ role: name, clone_from: cloneFrom });
      enqueueSnackbar(`Role "${name}" created`, { variant:'success' });
      setAddRoleDialog(false);
      // Reload roles and permissions
      const [rolesRes, permRes] = await Promise.all([authAPI.getRoles(), authAPI.getPermissions()]);
      if (rolesRes.data.success) setRoles(['admin', ...rolesRes.data.data.filter(r => r !== 'admin')]);
      if (permRes.data.success) {
        const map = {};
        permRes.data.data.forEach(p => {
          if (!map[p.role]) map[p.role] = {};
          map[p.role][p.module] = { access: p.access, own_data_only: !!p.own_data_only };
        });
        setPermissions(map);
      }
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error creating role', { variant:'error' }); }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Delete role "${role}"? This cannot be undone.`)) return;
    try {
      await authAPI.deleteRole(role);
      enqueueSnackbar(`Role "${role}" deleted`, { variant:'success' });
      setRoles(prev => prev.filter(r => r !== role));
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error deleting role', { variant:'error' }); }
  };

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','');

  const roleColor = (r) => ({ admin:'error', teacher:'primary', registrar:'warning', viewer:'default', class_teacher:'secondary' }[r] || 'default');

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} gutterBottom>Settings</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb:2, borderBottom:1, borderColor:'divider' }}
        variant="scrollable" scrollButtons="auto">
        <Tab label="School Info" />
        <Tab label="Academic Years" />
        <Tab label="Classes" />
        <Tab label="Users" />
        <Tab label="Permissions" />
      </Tabs>

      {/* ── SCHOOL INFO ── */}
      <TabPanel value={tab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>School Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><TextField label="School Name" value={settings.school_name||''} onChange={e=>setSettings({...settings,school_name:e.target.value})} /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="School Phone" value={settings.school_phone||''} onChange={e=>setSettings({...settings,school_phone:e.target.value})} /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="School Email" value={settings.school_email||''} onChange={e=>setSettings({...settings,school_email:e.target.value})} /></Grid>
                  <Grid item xs={12} sm={6}><TextField label="School Motto" value={settings.school_motto||''} onChange={e=>setSettings({...settings,school_motto:e.target.value})} /></Grid>
                  <Grid item xs={12}><TextField label="Address" value={settings.school_address||''} onChange={e=>setSettings({...settings,school_address:e.target.value})} multiline rows={2} /></Grid>
                  <Grid item xs={12}><TextField label="Report Footer Text" value={settings.report_footer||''} onChange={e=>setSettings({...settings,report_footer:e.target.value})} multiline rows={2} helperText="Printed at the bottom of student report cards" /></Grid>

                  <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Appearance</Typography></Divider></Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField select label="Theme Mode" value={settings.theme_mode||'light'} onChange={e=>setSettings({...settings,theme_mode:e.target.value})}>
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="dark">Dark</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField label="Primary Colour" type="color" value={settings.primary_color||'#1565c0'} onChange={e=>setSettings({...settings,primary_color:e.target.value})} InputLabelProps={{ shrink:true }} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField label="Secondary Colour" type="color" value={settings.secondary_color||'#e53935'} onChange={e=>setSettings({...settings,secondary_color:e.target.value})} InputLabelProps={{ shrink:true }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField label="Terms per Year" type="number" value={settings.term_count||3} onChange={e=>setSettings({...settings,term_count:parseInt(e.target.value)})} inputProps={{ min:1, max:4 }} />
                    <TextField label="Default CA Weight (%)" type="number"
                      value={settings.default_ca_weight||40}
                      onChange={e=>{const v=Math.min(100,Math.max(0,parseInt(e.target.value)||0));setSettings({...settings,default_ca_weight:v,default_exam_weight:100-v});}}
                      inputProps={{min:0,max:100}}
                      helperText={`Default exam weight: ${100-(parseInt(settings.default_ca_weight)||40)}%. Courses can override this.`}/>
                  </Grid>

                  <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">School Logo</Typography></Divider></Grid>
                  <Grid item xs={12} sx={{ display:'flex', alignItems:'center', gap:2, flexWrap:'wrap' }}>
                    {settings.logo && (
                      <Box component="img" src={`${API_BASE}/uploads/${settings.logo}`} alt="logo"
                        sx={{ width:70, height:70, borderRadius:2, objectFit:'cover', border:'1px solid', borderColor:'divider' }} />
                    )}
                    <Button variant="outlined" component="label" sx={{ height:40 }}>
                      {logo ? logo.name : settings.logo ? 'Replace Logo' : 'Upload Logo'}
                      <input type="file" accept="image/*" hidden onChange={e=>setLogo(e.target.files[0])} />
                    </Button>
                    {settings.logo && (
                      <Button variant="outlined" color="error" size="small" onClick={async()=>{
                        await settingsAPI.deleteLogo();
                        setSettings({...settings,logo:null});
                        enqueueSnackbar('Logo removed',{variant:'success'});
                      }}>Remove Logo</Button>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={saveSettings} disabled={saving} size="large"
                      sx={{ bgcolor:'#0f2b4a','&:hover':{bgcolor:'#1a3a5c'}, fontWeight:700 }}>
                      {saving ? <CircularProgress size={20} /> : 'Save Settings'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* ── ACADEMIC YEARS ── */}
      <TabPanel value={tab} index={1}>
        <Box sx={{ display:'flex', justifyContent:'space-between', mb:2 }}>
          <Typography variant="h6" fontWeight={700}>Academic Years & Terms</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={()=>setYearDialog(true)}
            sx={{ bgcolor:'#0f2b4a','&:hover':{bgcolor:'#1a3a5c'} }}>Add Year/Term</Button>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }} icon={<CheckCircle fontSize="small" />}>
          The <strong>Current</strong> term is automatically detected based on today's date falling within the term's start and end dates. Use <strong>Set Current</strong> to manually override if needed.
        </Alert>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                  {['Year','Term','Start Date','End Date','Status','Action'].map(h=>(
                    <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {academicYears.map((y,i) => {
                  const today = new Date();
                  const start = y.start_date ? new Date(y.start_date) : null;
                  const end   = y.end_date   ? new Date(y.end_date)   : null;
                  const isInDateRange = start && end && today >= start && today <= end;
                  return (
                  <TableRow key={y.id} hover sx={{ bgcolor: y.is_current ? '#f0fdf4' : i%2===0?'white':'#f7f8fc' }}>
                    <TableCell><Typography fontWeight={700}>{y.year_label}</Typography></TableCell>
                    <TableCell>{y.term}</TableCell>
                    <TableCell>{y.start_date ? new Date(y.start_date).toLocaleDateString('en-GB') : '—'}</TableCell>
                    <TableCell>{y.end_date ? new Date(y.end_date).toLocaleDateString('en-GB') : '—'}</TableCell>
                    <TableCell>
                      {y.is_current
                        ? <Chip label={isInDateRange ? '✓ Current (auto)' : '✓ Current (manual)'} color="success" size="small" sx={{ fontWeight:700 }} />
                        : isInDateRange
                          ? <Chip label="In range — loading…" color="warning" size="small" variant="outlined" />
                          : <Chip label="Inactive" size="small" variant="outlined" />
                      }
                    </TableCell>
                    <TableCell>
                      {!y.is_current && (
                        <Button size="small" startIcon={<CheckCircle />}
                          onClick={() => setCurrentYear(y.id)}
                          sx={{ fontSize:11 }}>
                          Set Current
                        </Button>
                      )}
                      <Tooltip title="Edit dates">
                        <IconButton size="small" onClick={() => openEditYear(y)} sx={{ ml: 0.5 }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => deleteYear(y.id)} sx={{ ml: 0.5 }}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {!academicYears.length && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py:4, color:'text.secondary', fontStyle:'italic' }}>No academic years created yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Dialog open={yearDialog} onClose={()=>{ setYearDialog(false); setEditingYear(null); setYearForm({ year_label:'', term:'Term 1', start_date:'', end_date:'' }); }} maxWidth="sm" fullWidth>
          <DialogTitle>{editingYear ? 'Edit Academic Year / Term' : 'Add Academic Year / Term'}</DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              The system will automatically set this as the current term when today's date falls within the start and end dates.
            </Alert>
            <Grid container spacing={2} sx={{ pt:1 }}>
              <Grid item xs={12} sm={6}><TextField label="Year Label (e.g. 2024/2025)" value={yearForm.year_label} onChange={e=>setYearForm({...yearForm,year_label:e.target.value})} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Term" value={yearForm.term} onChange={e=>setYearForm({...yearForm,term:e.target.value})}>
                  {['Term 1','Term 2','Term 3','Semester 1','Semester 2'].map(t=><MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField label="Start Date" type="date" value={yearForm.start_date} onChange={e=>setYearForm({...yearForm,start_date:e.target.value})} InputLabelProps={{ shrink:true }} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="End Date" type="date" value={yearForm.end_date} onChange={e=>setYearForm({...yearForm,end_date:e.target.value})} InputLabelProps={{ shrink:true }} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>{ setYearDialog(false); setEditingYear(null); }}>Cancel</Button>
            <Button variant="contained" onClick={createYear}>{editingYear ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ── CLASSES ── */}
      <TabPanel value={tab} index={2}>
        <Box sx={{ display:'flex', justifyContent:'space-between', mb:2 }}>
          <Typography variant="h6" fontWeight={700}>Classes / Streams</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateClass}
            sx={{ bgcolor:'#0f2b4a','&:hover':{bgcolor:'#1a3a5c'} }}>Add Class</Button>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                  {['Class','Stream','Level','Class Teacher','Students','Capacity','Active','Actions'].map(h=>(
                    <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.map((c,i) => (
                  <TableRow key={c.id} hover sx={{ bgcolor:i%2===0?'white':'#f7f8fc' }}>
                    <TableCell><Typography fontWeight={700}>{c.name}</Typography></TableCell>
                    <TableCell>{c.stream||'—'}</TableCell>
                    <TableCell>{c.level||'—'}</TableCell>
                    <TableCell>{c.teacher_first?`${c.teacher_first} ${c.teacher_last}`:'—'}</TableCell>
                    <TableCell><Chip label={c.student_count||0} color="primary" size="small" sx={{ fontWeight:700 }} /></TableCell>
                    <TableCell>{c.capacity}</TableCell>
                    <TableCell>
                      <Chip label={c.is_active?'Active':'Inactive'} size="small"
                        color={c.is_active?'success':'default'} sx={{ fontWeight:700 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit"><IconButton size="small" onClick={()=>openEditClass(c)}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={()=>deleteClass(c.id)}><Cancel fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!classes.length && (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py:4, color:'text.secondary', fontStyle:'italic' }}>No classes created yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Dialog open={classDialog} onClose={()=>setClassDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editingClass?'Edit Class':'Add Class'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt:1 }}>
              <Grid item xs={12} sm={6}><TextField label="Class Name *" value={classForm.name} onChange={e=>setClassForm({...classForm,name:e.target.value})} /></Grid>
              <Grid item xs={12} sm={6}><TextField label="Stream (e.g. A, Blue, Science)" value={classForm.stream} onChange={e=>setClassForm({...classForm,stream:e.target.value})} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select label="Level" value={classForm.level_id || ''} onChange={e=>setClassForm({...classForm, level_id:e.target.value, level: levels.find(l=>l.id==e.target.value)?.name || ''})}>
                  <MenuItem value="">No level</MenuItem>
                  {levels.map(l=><MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField label="Capacity" type="number" value={classForm.capacity} onChange={e=>setClassForm({...classForm,capacity:parseInt(e.target.value)})} /></Grid>
              <Grid item xs={12}>
                <TextField select label="Class Teacher (optional)" value={classForm.class_teacher_id} onChange={e=>setClassForm({...classForm,class_teacher_id:e.target.value})}>
                  <MenuItem value="">None</MenuItem>
                  {teachers.map(t=><MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name}{t.username ? ` (${t.username})` : ' — no account'}</MenuItem>)}
                </TextField>
                {classForm.class_teacher_id && (
                  <Alert severity="info" sx={{ mt:1, py:0.5, fontSize:12 }}>
                    This teacher will be linked as class teacher. Make sure their account has the <strong>class_teacher</strong> role in Settings → Permissions.
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={!!classForm.is_active} onChange={e=>setClassForm({...classForm,is_active:e.target.checked})} />} label="Active class" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setClassDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveClass}>{editingClass?'Update':'Create'}</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ── USERS ── */}
      <TabPanel value={tab} index={3}>
        <Box sx={{ display:'flex', justifyContent:'space-between', mb:2 }}>
          <Typography variant="h6" fontWeight={700}>System Users</Typography>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateUser}
            sx={{ bgcolor:'#0f2b4a','&:hover':{bgcolor:'#1a3a5c'} }}>Add User</Button>
        </Box>
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor:'#0f2b4a' }}>
                  {['Name','Username','Role','Email','Linked Teacher','Last Login','Status','Actions'].map(h=>(
                    <TableCell key={h} sx={{ color:'rgba(255,255,255,.8)', fontWeight:700, fontSize:11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u,i) => (
                  <TableRow key={u.id} hover sx={{ bgcolor:i%2===0?'white':'#f7f8fc' }}>
                    <TableCell>
                      <Typography fontWeight={700} fontSize={13}>{u.full_name}</Typography>
                      {u.must_change_password ? <Chip label="Must change pwd" size="small" color="warning" sx={{ height:16, fontSize:8, mt:0.3 }} /> : null}
                    </TableCell>
                    <TableCell><Chip label={u.username} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={u.role} size="small" color={roleColor(u.role)} sx={{ fontWeight:700 }} /></TableCell>
                    <TableCell>{u.email||'—'}</TableCell>
                    <TableCell>
                      {u.teacher_id ? (
                        <Chip label={`${u.teacher_first} ${u.teacher_last}`} size="small" color="info" />
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize:11 }}>
                      {u.last_login ? new Date(u.last_login).toLocaleString('en-GB', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Chip label={u.is_active?'Active':'Disabled'} size="small"
                        color={u.is_active?'success':'default'} sx={{ fontWeight:700 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Role / Email">
                        <IconButton size="small" onClick={()=>openEditUser(u)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton size="small" color="warning" onClick={()=>{ setResetUserId(u.id); setResetPwd('123456'); setResetDialog(true); }}>
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {u.id !== currentUser?.id && (
                        <Tooltip title={u.is_active?'Disable Account':'Enable Account'}>
                          <IconButton size="small" color={u.is_active?'error':'success'} onClick={()=>toggleUser(u.id)}>
                            {u.is_active ? <Lock fontSize="small" /> : <LockOpen fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* User dialog */}
        <Dialog open={userDialog} onClose={()=>setUserDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <AdminPanelSettings /> {editingUser?'Edit User':'Add User'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt:1 }}>
              <Grid item xs={12}><TextField label="Full Name *" value={userForm.full_name} onChange={e=>setUserForm({...userForm,full_name:e.target.value})} /></Grid>
              {!editingUser && <Grid item xs={12} sm={6}><TextField label="Username *" value={userForm.username} onChange={e=>setUserForm({...userForm,username:e.target.value})} /></Grid>}
              {!editingUser && <Grid item xs={12} sm={6}><TextField label="Initial Password *" value={userForm.password} onChange={e=>setUserForm({...userForm,password:e.target.value})} helperText="User must change on first login" /></Grid>}
              <Grid item xs={12} sm={6}>
                <TextField select label="Role *" value={userForm.role} onChange={e=>setUserForm({...userForm,role:e.target.value})}>
                  {roles.map(r=><MenuItem key={r} value={r} sx={{ textTransform:'capitalize' }}>{r.replace(/_/g,' ')}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}><TextField label="Email" type="email" value={userForm.email} onChange={e=>setUserForm({...userForm,email:e.target.value})} /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setUserDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={saveUser} disabled={userSaving}>
              {userSaving?<CircularProgress size={20}/>:editingUser?'Update':'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset password dialog */}
        <Dialog open={resetDialog} onClose={()=>setResetDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle><KeyIcon sx={{ mr:1, verticalAlign:'middle', color:'#e67e22' }} />Reset Password</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" sx={{ mb:2 }}>User will be required to change this password on next login.</Alert>
            <TextField label="New Password" value={resetPwd} onChange={e=>setResetPwd(e.target.value)} fullWidth helperText="Minimum 6 characters" />
          </DialogContent>
          <DialogActions>
            <Button onClick={()=>setResetDialog(false)}>Cancel</Button>
            <Button variant="contained" color="warning" onClick={doResetPassword}>Reset Password</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* ── PERMISSIONS ── */}
      <TabPanel value={tab} index={4}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Module Permissions</Typography>
            <Typography variant="body2" color="text.secondary">Control what each role can access. Changes take effect on next login.</Typography>
          </Box>
          <Box sx={{ display:'flex', gap:1 }}>
            <Tooltip title="Reload from server">
              <IconButton onClick={loadAll}><Refresh /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={savePermissions} disabled={permSaving}
              sx={{ bgcolor:'#0f2b4a','&:hover':{bgcolor:'#1a3a5c'}, fontWeight:700 }}>
              {permSaving?<CircularProgress size={20}/>:'Save Permissions'}
            </Button>
          </Box>
        </Box>

        <Card>
          <TableContainer sx={{ overflowX:'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight:800, bgcolor:'#0f2b4a', color:'white', minWidth:140, fontSize:11 }}>Module</TableCell>
                  {roles.map(r => (
                    <TableCell key={r} align="center" sx={{ fontWeight:800, bgcolor:'#0f2b4a', color:'rgba(255,255,255,.8)', minWidth:140, fontSize:11 }}>
                      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center', gap:0.5 }}>
                        <Chip label={r.replace(/_/g,' ')} size="small" color={roleColor(r)} sx={{ fontWeight:800, textTransform:'capitalize' }} />
                        {!['admin','teacher','registrar','viewer'].includes(r) && (
                          <Tooltip title={`Delete "${r}" role`}>
                            <IconButton size="small" sx={{ color:'rgba(255,100,100,.7)', p:0.2 }}
                              onClick={() => handleDeleteRole(r)}>
                              <Cancel sx={{ fontSize:14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  ))}
                  <TableCell sx={{ bgcolor:'#0f2b4a', minWidth:100 }}>
                    <Tooltip title="Add new role">
                      <IconButton size="small" sx={{ color:'rgba(255,255,255,.7)', border:'1px dashed rgba(255,255,255,.4)', borderRadius:1 }}
                        onClick={() => { setNewRoleName(''); setCloneFrom('viewer'); setAddRoleDialog(true); }}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MODULES.map((mod, i) => (
                  <TableRow key={mod.key} hover sx={{ bgcolor:i%2===0?'white':'#f7f8fc' }}>
                    <TableCell sx={{ fontWeight:700, fontSize:12 }}>{mod.label}</TableCell>
                    {roles.map(role => {
                      const perm = permissions[role]?.[mod.key] || { access:'none', own_data_only:false };
                      const isAdmin = role === 'admin';
                      return (
                        <TableCell key={role} align="center" sx={{ p:1 }}>
                          <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap:0.5 }}>
                            <TextField select size="small" value={perm.access}
                              disabled={isAdmin}
                              onChange={e => updatePerm(role, mod.key, 'access', e.target.value)}
                              sx={{ width:90, '& .MuiInputBase-input':{ fontSize:11, py:'5px' } }}>
                              {ACCESS_OPTS.map(a => (
                                <MenuItem key={a} value={a}>
                                  <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                                    <Box sx={{ width:8, height:8, borderRadius:'50%',
                                      bgcolor: a==='full'?'#1a7a3c':a==='view'?'#1565c0':'#ccc' }} />
                                    <Typography fontSize={10} sx={{ textTransform:'capitalize' }}>{a}</Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </TextField>
                            {perm.access !== 'none' && role !== 'admin' && (
                              <FormControlLabel
                                control={<Switch size="small" checked={!!perm.own_data_only}
                                  onChange={e => updatePerm(role, mod.key, 'own_data_only', e.target.checked)} />}
                                label={<Typography fontSize={9} color="text.secondary">Own data only</Typography>}
                                sx={{ m:0 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Alert severity="info" sx={{ mt:2 }}>
          <strong>none</strong> — module hidden from sidebar. &nbsp;
          <strong>view</strong> — read-only access. &nbsp;
          <strong>full</strong> — can create, edit and delete. &nbsp;
          <strong>Own data only</strong> — teacher sees only their assigned classes/subjects.
        </Alert>

        {/* Add Role Dialog */}
        <Dialog open={addRoleDialog} onClose={() => setAddRoleDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Add New Role</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt:1 }}>
              <Grid item xs={12}>
                <TextField label="Role Name" fullWidth size="small"
                  value={newRoleName}
                  onChange={e => setNewRoleName(e.target.value)}
                  helperText="e.g. class_teacher, hod, deputy — lowercase, underscores only" />
              </Grid>
              <Grid item xs={12}>
                <TextField select label="Copy Permissions From" fullWidth size="small"
                  value={cloneFrom} onChange={e => setCloneFrom(e.target.value)}>
                  {roles.filter(r => r !== 'admin').map(r => (
                    <MenuItem key={r} value={r} sx={{ textTransform:'capitalize' }}>{r.replace(/_/g,' ')}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddRoleDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddRole}>Create Role</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>
    </Box>
  );
}
